import React, { useState, useEffect, useMemo } from 'react';
import { RotateCcw, Columns, X, ArrowRight, Edit2, CheckCircle, Loader2 } from 'lucide-react';
import './VersionDetailsModal.css';
import { formatLocalDateTime } from '../../../../../../utils/date';

const VersionDetailsModal = ({ 
    isOpen, 
    onClose, 
    versionData, 
    activeSection, 
    versionsList,
    onRevert,
    onCompare,
    onRename,
    onApprove = null,
    isManager = false,
    currentSection = null,
    isApproving = false
}) => {
    // Determine section status accurately
    const rawStatusCode = currentSection?.status?.code ?? currentSection?.status_code ?? currentSection?.statusCode;
    const currentStatusStr = String(currentSection?.status?.label || currentSection?.status || '').toLowerCase();
    const currentStatusCode = Number(rawStatusCode) || (
        currentStatusStr.includes('submitted') && !currentStatusStr.includes('not submitted') ? 53 :
        currentStatusStr.includes('approved') ? 54 :
        currentStatusStr.includes('revision') || currentStatusStr.includes('needs revision') ? 55 :
        currentStatusStr.includes('progress') ? 52 :
        0
    );
    const isSubmitted = currentStatusCode === 53;
    const isApproved = currentStatusCode === 54;

    // Find newest TL Approved version number to show "Live on main document" badge
    const newestTLApprovedVersionNumber = useMemo(() => {
        if (!Array.isArray(versionsList) || versionsList.length === 0) return null;
        let maxVer = null;
        for (const v of versionsList) {
            const isTL = v.change_type === 'tl_approved' || 
                         (v.change_type && String(v.change_type).toLowerCase() === 'tl_approved') ||
                         (v.change_type_label && String(v.change_type_label).toLowerCase().includes('tl approved'));
            if (isTL) {
                const num = Number(v.version_number ?? v.version_id ?? v.id);
                if (maxVer === null || num > maxVer) {
                    maxVer = num;
                }
            }
        }
        return maxVer;
    }, [versionsList]);

    const isLiveOnMain = versionData ? (Number(versionData.version_number) === newestTLApprovedVersionNumber) : false;
    const isFormalSubmission = Boolean(
        (versionData?.comment && String(versionData.comment).toLowerCase().includes('formal submission')) ||
        (versionData?.reason && String(versionData.reason).toLowerCase().includes('formal submission')) ||
        (versionData?.label && String(versionData.label).toLowerCase().includes('formal submission'))
    );

    const [isSelectingCompare, setIsSelectingCompare] = useState(false);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const [tempLabel, setTempLabel] = useState('');

    useEffect(() => {
        if (versionData) {
            setTempLabel(`Version ${versionData.version_number}`);
        }
    }, [versionData]);

    if (!isOpen || !versionData) return null;

    const { version_number, section_label, content, created_at, created_by } = versionData;
    const authorDisplay = versionData.created_by_name || (created_by === '2acf4050-1290-4cb2-b8a9-c44294099062' ? 'System' : (created_by || 'System'));

    const htmlContent = typeof content === 'object' && content !== null 
        ? (content.content || '') 
        : (content || '');

    const handleLabelSubmit = () => {
        if (tempLabel.trim() && tempLabel !== `Version ${version_number}`) {
            onRename(version_number, tempLabel);
        }
        setIsEditingLabel(false);
    };

    const formatDate = (dateStr) => {
        return formatLocalDateTime(dateStr);
    };

    return (
        <div className="version-preview-overlay">
            <div className="version-preview-container">
                <div className="version-preview-header">
                    <div className="header-info">
                        <div className="title-edit-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isEditingLabel ? (
                                <input
                                    type="text"
                                    className="title-edit-input"
                                    value={tempLabel}
                                    onChange={(e) => setTempLabel(e.target.value)}
                                    onBlur={handleLabelSubmit}
                                    onKeyDown={(e) => e.key === 'Enter' && handleLabelSubmit()}
                                    autoFocus
                                />
                            ) : (
                                <h2 className="title-display" onClick={() => setIsEditingLabel(true)}>
                                    {tempLabel}
                                    <Edit2 size={14} className="edit-icon-hint" />
                                </h2>
                            )}
                            {isFormalSubmission && (
                                <span className="v-submission-badge" title="Employee Formal Submission">
                                    Formal submission
                                </span>
                            )}
                            {isLiveOnMain && (
                                <span className="v-live-badge" title="This approved version is currently live on the main RFP document">
                                    <span className="v-live-dot" />
                                    Live on main document
                                </span>
                            )}
                        </div>
                        <p>{section_label || activeSection}</p>
                    </div>
                    <div className="header-actions vdm-header-actions">
                        {!isSelectingCompare && (
                            <>
                                {isManager && !isApproved && (
                                    <button
                                        type="button"
                                        className={`action-btn vdm-action-btn vdm-btn-approve approve ${isSubmitted ? 'ready' : 'disabled'}`}
                                        onClick={() => {
                                            if (!isSubmitted) return;
                                            onApprove && onApprove(version_number);
                                        }}
                                        disabled={!isSubmitted || isApproving}
                                        title={!isSubmitted ? "Employee must submit before TL approval" : `Approve Version ${version_number} as main document`}
                                    >
                                        {isApproving ? (
                                            <Loader2 size={15} className="animate-spin" />
                                        ) : (
                                            <CheckCircle size={15} color={isSubmitted ? '#15803D' : '#64748B'} />
                                        )}
                                        <span>Approve this version</span>
                                    </button>
                                )}
                                <button 
                                    type="button" 
                                    className="action-btn vdm-action-btn vdm-btn-revert revert" 
                                    onClick={() => onRevert(versionData)}
                                >
                                    <RotateCcw size={15} />
                                    <span>Revert</span>
                                </button>
                                <button 
                                    type="button" 
                                    className="action-btn vdm-action-btn vdm-btn-compare compare" 
                                    onClick={() => setIsSelectingCompare(true)}
                                >
                                    <Columns size={15} />
                                    <span>Compare</span>
                                </button>
                            </>
                        )}
                        <button type="button" className="close-x-btn" onClick={onClose} title="Close">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="version-preview-body">
                    {isSelectingCompare ? (
                        <div className="compare-selection-view">
                            <div className="selection-header">
                                <h3>Select version to compare with v{version_number}</h3>
                                <button className="back-view-btn" onClick={() => setIsSelectingCompare(false)}>
                                    Back to View
                                </button>
                            </div>
                            <div className="selection-list">
                                {versionsList
                                    .filter(v => v.version_number !== version_number)
                                    .map((v, i) => (
                                        <div 
                                            key={i} 
                                            className="selection-item"
                                            onClick={() => {
                                                onCompare(version_number, v.version_number);
                                                onClose();
                                            }}
                                        >
                                            <div className="item-meta">
                                                <span className="v-name">Version {v.version_number}</span>
                                                <span className="v-author-date">
                                                    {v.created_by_name || 'System'} · {formatDate(v.created_at)}
                                                </span>
                                            </div>
                                            <ArrowRight size={18} className="item-arrow" />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ) : (
                        <div className="content-card final-view-area" dangerouslySetInnerHTML={{ __html: htmlContent }} />
                    )}
                </div>

                <div className="version-preview-footer">
                    <span>Created by {authorDisplay} on {formatDate(created_at)}</span>
                    <button className="close-btn-main" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default VersionDetailsModal;
